import { useState, useEffect } from 'react';
import { billingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { CreditCard, Download, CheckCircle, Zap } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

export default function BillingPage() {
  const { merchant } = useAuth();
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    Promise.all([billingAPI.getPlans(), billingAPI.getInvoices()]).then(([p, i]) => {
      setPlans(p.data.data);
      setInvoices(i.data.data.invoices);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const downloadInvoice = async (id, number) => {
    try {
      const { data } = await billingAPI.downloadInvoicePDF(id);
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `invoice-${number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (_) { toast.error('Could not download invoice'); }
  };

  const statusBadge = (status) => {
    const map = { paid: 'badge-green', sent: 'badge-blue', overdue: 'badge-red', draft: 'badge-gray', cancelled: 'badge-gray' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
  };

  const currentPlan = merchant?.subscription?.plan || 'free';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Billing</h1>
          <p className="page-subtitle">Manage your subscription and view invoices</p>
        </div>
      </div>

      <div className="page-body">
        <div className="tabs">
          {['overview', 'plans', 'invoices'].map(t => (
            <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div>
            <div className="grid-2" style={{ gap: 16, marginBottom: 20 }}>
              <div className="card">
                <div className="card-title" style={{ marginBottom: 12 }}>Current Plan</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 800, textTransform: 'capitalize' }}>{currentPlan}</span>
                  <span className={`badge ${merchant?.subscription?.status === 'active' ? 'badge-green' : 'badge-yellow'}`}>
                    {merchant?.subscription?.status || 'active'}
                  </span>
                </div>
                {merchant?.subscription?.endDate && (
                  <p style={{ fontSize: '0.82rem', marginTop: 8 }}>Renews {format(new Date(merchant.subscription.endDate), 'MMM d, yyyy')}</p>
                )}
                <button className="btn btn-primary btn-sm" style={{ marginTop: 14 }} onClick={() => setTab('plans')}>
                  <Zap size={13} /> Upgrade Plan
                </button>
              </div>
              <div className="card">
                <div className="card-title" style={{ marginBottom: 12 }}>This Month's Usage</div>
                {[
                  { label: 'Messages', used: merchant?.usage?.messagesThisMonth || 0, total: merchant?.limits?.messagesPerMonth || 1000, color: 'var(--primary)' },
                  { label: 'AI Replies', used: merchant?.usage?.aiRepliesThisMonth || 0, total: merchant?.limits?.aiRepliesPerMonth || 500, color: 'var(--primary)' },
                ].map(item => (
                  <div key={item.label} style={{ marginBottom: 12 }}>
                    <div className="flex-between" style={{ marginBottom: 5 }}>
                      <span style={{ fontSize: '0.82rem' }}>{item.label}</span>
                      <span style={{ fontSize: '0.78rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                        {item.used.toLocaleString()} / {item.total === 999999 ? '∞' : item.total.toLocaleString()}
                      </span>
                    </div>
                    <div style={{ height: 6, background: 'var(--border)', borderRadius: 99 }}>
                      <div style={{ height: '100%', width: `${Math.min(100, (item.used / item.total) * 100)}%`, background: item.color, borderRadius: 99 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'plans' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
              {plans.map(plan => (
                <div key={plan.slug} className={`plan-card ${plan.slug === currentPlan ? 'selected' : ''}`}>
                  {plan.slug === 'professional' && (
                    <div style={{ marginBottom: 10 }}>
                      <span className="badge badge-mauve">Most Popular</span>
                    </div>
                  )}
                  {plan.slug === currentPlan && <span className="badge badge-green" style={{ marginBottom: 10 }}>Current Plan</span>}
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.1rem', marginBottom: 4, textTransform: 'capitalize' }}>{plan.name}</div>
                  <div className="plan-price">
                    {plan.price.monthly === 0 ? 'Free' : `$${(plan.price.monthly / 100).toFixed(0)}`}
                    {plan.price.monthly > 0 && <span style={{ fontSize: '1rem', fontWeight: 400, color: 'var(--text-muted)' }}>/mo</span>}
                  </div>
                  <ul className="plan-features">
                    {plan.features.map(f => (
                      <li key={f}><CheckCircle size={13} /> {f}</li>
                    ))}
                  </ul>
                  <button className={`btn w-full ${plan.slug === currentPlan ? 'btn-secondary' : 'btn-primary'}`}
                    disabled={plan.slug === currentPlan}
                    style={{ marginTop: 16, justifyContent: 'center' }}>
                    {plan.slug === currentPlan ? 'Current Plan' : plan.slug === 'free' ? 'Downgrade' : 'Upgrade'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'invoices' && (
          <div className="card" style={{ padding: 0 }}>
            <div className="table-container">
              {invoices.length === 0 ? (
                <div className="empty-state" style={{ padding: 60 }}><CreditCard size={36} /><h3>No invoices yet</h3></div>
              ) : (
                <table>
                  <thead>
                    <tr><th>Invoice</th><th>Period</th><th>Amount</th><th>Due Date</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv._id}>
                        <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}>{inv.invoiceNumber}</span></td>
                        <td><span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{format(new Date(inv.periodStart), 'MMM d')} – {format(new Date(inv.periodEnd), 'MMM d, yyyy')}</span></td>
                        <td><strong>${(inv.total / 100).toFixed(2)}</strong></td>
                        <td><span style={{ fontSize: '0.8rem' }}>{format(new Date(inv.dueDate), 'MMM d, yyyy')}</span></td>
                        <td>{statusBadge(inv.status)}</td>
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => downloadInvoice(inv._id, inv.invoiceNumber)}>
                            <Download size={13} /> PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
